import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as AddIcon } from "../../../assets/icons/fi-plus-circle.svg";
import { StatusBadge } from "../../../constants/statusConstants";
import { AuthContext } from "../../../contexts/AuthContext";
import { postsService } from "../../../services/api/postsService";
import { getCleanFileName } from "../../../utils/fileUtils";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import CustomTable from "../components/CustomTable";
import PostPopup from "./components/PostPopup";

const PostsMangement = () => {
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [canDelete, setCanDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [currentPosts, setCurrentPosts] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page")) || 1;
  const [searchInput, setSearchInput] = useState(search);

  const fetchData = useCallback(
    async (searchValue = "", currentPage = 1) => {
      try {
        setLoading(true);
        const response = await postsService.get(searchValue, token, currentPage);
        setPosts(response?.data || []);
        setTotalPages(response.data?.pagination?.totalPages || 1);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Không thể kết nối tới server"
        );
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    if (searchInput) {
      params.set("search", searchInput);
    } else {
      params.delete("search");
    }
    params.set("page", 1);
    setSearchParams(params);
  }, [searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchInput !== (searchParams.get("search") || "")) {
        handleSearch();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, searchParams, handleSearch]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleAddPost = async (FormData) => {
    try {
      setLoading(true);
      const response = await postsService.create(FormData, token);
      toast.success("Bài viết được tạo thành công!");
      fetchData(search, page);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server"
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeletingPostId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingPostId) return;
    try {
      setIsDeleting(true);
      await postsService.delete(deletingPostId, token);
      toast.success("Xóa bài viết thành công");
      fetchData(search, page);
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server"
      );
    } finally {
      setIsDeleting(false);
      setDeletingPostId(null);
    }
  };

  const showEditPopup = (post) => {
    setCurrentPosts(post);
    setIsEditPopupOpen(true);
  };

  useEffect(() => {
    fetchData(search, page);
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, search, page, user?.role]);

  const columns = useMemo(
    () => [
      {
        key: "stt",
        title: "STT",
        minWidth: "60px",
        align: "center",
        render: (_, __, index) => (page - 1) * 10 + index + 1,
      },
      {
        key: "name",
        title: "Tên tài liệu",
        minWidth: "250px",
        render: (value) => <p className="truncate font-medium">{value}</p>,
      },
      {
        key: "txtUrl",
        title: "Nội dung bài viết",
        minWidth: "150px",
        render: (value, item) => {
          if (!value || value.trim() === "" || value === "<p><br></p>") {
            return <span className="text-gray-400">Không có bài viết</span>;
          }
          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                showEditPopup(item);
              }}
              className="inline-block text-blue-600 hover:text-blue-700 font-medium hover:underline cursor-pointer transition-colors"
            >
              Xem nội dung
            </span>
          );
        },
      },
      {
        key: "fileUrl",
        title: "File đính kèm",
        minWidth: "150px",
        render: (value) => {
          if (!value) {
            return <span className="text-gray-400">--</span>;
          }
          return (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-blue-600 hover:text-blue-700 font-medium hover:underline cursor-pointer transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {getCleanFileName(value)}
            </a>
          );
        },
      },
      {
        key: "status",
        title: "Trạng thái",
        minWidth: "120px",
        render: (value) => <StatusBadge status={value || "Hoạt động"} />,
      },
    ],
    [navigate]
  );

  return (
    <>
      <div className="flex flex-col h-full min-h-0 gap-8 relative">
        <div className="shrink-0 flex justify-between items-center">
          <div className="relative w-1/3">
            <input
              type="text"
              placeholder="Tìm kiếm tài liệu"
              className="border border-gray-200 rounded-lg px-4 py-2 focus:outline-blue-500 w-full"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <span
              className="absolute right-3 top-2.5 cursor-pointer"
              onClick={handleSearch}
            >
              <img src="/icons/search-black.svg" alt="Search icon" />
            </span>
          </div>

          <button
            onClick={() => setIsPopupOpen(true)}
            className="bg-[#0457C2] hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-be-vietnam-medium flex items-center gap-2"
          >
            <AddIcon className="w-5 h-5" />
            Thêm mới
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-visible">
          <div className="h-full min-h-0 overflow-auto p-[2px]">
            <CustomTable
              data={posts}
              columns={columns}
              loading={loading}
              currentPage={page}
              setCurrentPage={(newPage) => {
                const params = new URLSearchParams(searchParams);
                params.set("page", newPage);
                setSearchParams(params);
              }}
              totalPages={totalPages}
              setEdit={true}
              handleEdit={(id, item) => showEditPopup(item)}
              setDelete={canDelete}
              handleDelete={(id) => confirmDelete(id)}
              onRowClick={(item) => navigate(`/tra-cuu/bai-viet/${item._id}`)}
              rowIdKey="_id"
            />
          </div>
        </div>
      </div>

      {isPopupOpen && (
        <PostPopup
          mode="add"
          closePopup={() => setIsPopupOpen(false)}
          handleAddPost={handleAddPost}
        />
      )}

      {isEditPopupOpen && currentPosts && (
        <PostPopup
          mode="edit"
          post_id={currentPosts._id}
          closePopup={() => setIsEditPopupOpen(false)}
          onSuccess={() => fetchData(search)}
        />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Xác nhận xóa tài liệu"
        message="Bạn có chắc chắn muốn xóa tài liệu này không? Hành động này sẽ gỡ bỏ tài liệu khỏi hệ thống."
        loading={isDeleting}
      />
    </>
  );
};

export default PostsMangement;
